import React from 'react';
import { Reminder, REMINDER_PRESETS, createReminder, formatReminderPreview } from '../../types/reminderTypes';
import { EVENT_TYPE_CONFIGS, EventType } from '../../types/eventTypes';

interface ReminderSelectorProps {
    reminders: Reminder[];
    onChange: (reminders: Reminder[]) => void;
    eventType?: EventType;
    eventDate?: Date;
}

const ReminderSelector: React.FC<ReminderSelectorProps> = ({
    reminders,
    onChange,
    eventType,
    eventDate,
}) => {
    // Get suggested reminders based on event type
    const suggestedDays = eventType
        ? EVENT_TYPE_CONFIGS[eventType]?.defaultReminders || [7, 3, 1]
        : [7, 3, 1];

    const toggleReminder = (daysBefore: number) => {
        const existing = reminders.find(r => r.daysBefore === daysBefore);

        if (existing) {
            // Remove if exists
            onChange(reminders.filter(r => r.daysBefore !== daysBefore));
        } else {
            // Add new reminder
            onChange([...reminders, createReminder(daysBefore)]);
        }
    };

    const isSelected = (daysBefore: number) => {
        return reminders.some(r => r.daysBefore === daysBefore && r.enabled);
    };

    const activeCount = reminders.filter(r => r.enabled).length;

    return (
        <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
                <span className="text-sm text-white/80 font-medium">
                    🔔 Nhắc nhở
                </span>
                {activeCount > 0 && (
                    <span className="text-xs text-blue-400">
                        {activeCount} nhắc nhở đã bật
                    </span>
                )}
            </div>

            {/* Preset Chips */}
            <div className="flex flex-wrap gap-2">
                {REMINDER_PRESETS.map(preset => {
                    const selected = isSelected(preset.daysBefore);
                    const isSuggested = suggestedDays.includes(preset.daysBefore);

                    return (
                        <button
                            key={preset.daysBefore}
                            type="button"
                            onClick={() => toggleReminder(preset.daysBefore)}
                            className={`
                                px-3 py-2 rounded-full text-sm font-medium
                                transition-all duration-200 border
                                ${selected
                                    ? 'bg-blue-500 border-blue-500 text-white'
                                    : isSuggested
                                        ? 'bg-blue-500/20 border-blue-500/50 text-blue-300 hover:bg-blue-500/30'
                                        : 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20'
                                }
                            `}
                        >
                            {selected && <span className="mr-1">✓</span>}
                            {preset.label}
                            {isSuggested && !selected && (
                                <span className="ml-1 text-xs opacity-70">★</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Reminder Preview */}
            {eventDate && reminders.length > 0 && (
                <div className="bg-white/5 rounded-lg p-3 space-y-1">
                    <span className="text-xs text-white/50">Sẽ nhắc vào:</span>
                    <div className="flex flex-wrap gap-2">
                        {reminders
                            .filter(r => r.enabled)
                            .sort((a, b) => b.daysBefore - a.daysBefore)
                            .map(r => (
                                <span
                                    key={r.id}
                                    className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded"
                                >
                                    📅 {formatReminderPreview(eventDate, r.daysBefore)}
                                </span>
                            ))
                        }
                    </div>
                </div>
            )}

            {/* Suggested hint */}
            {eventType && activeCount === 0 && (
                <p className="text-xs text-white/50">
                    💡 Gợi ý cho {EVENT_TYPE_CONFIGS[eventType]?.label}: {suggestedDays.join('/')} ngày trước
                </p>
            )}
        </div>
    );
};

export default ReminderSelector;
